import { useState, useEffect, useMemo } from 'react'
import { useWallet } from '@solana/wallet-adapter-react'
import { PublicKey } from '@solana/web3.js'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { X, ArrowRight, Loader2, CheckCircle, Wallet, AlertTriangle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api } from '../../services/api'
import { solanaService } from '../../services/solana.service'
import { toast } from 'sonner'
import { useAuth } from '../../hooks/useAuth'
import { useConfig } from '../../contexts/ConfigContext'
import { BLOCKCHAIN_CONFIG } from '../../config/app.config'
import type { InvestmentCalculation, PaymentMethod } from '../../types'

interface InvestmentModalProps {
  isOpen: boolean
  onClose: () => void
  vaultId: string
  calculation: InvestmentCalculation
  usdtAmount: number
  laikaAmount: number
  takaraAmount?: number // TAKARA boost amount (v3.1)
  takaraPrice?: number // Current TAKARA price in USD
  boostType?: 'LAIKA' | 'TAKARA' // Which token is used for boost
  acceptedPayments?: string // Only "USDT" - TAKARA is boost-only, not a payment method
}

type Step = 'review' | 'transfer' | 'confirm' | 'success'

// Token mints - single source of truth from app.config.ts
const USDT_MINT = new PublicKey(BLOCKCHAIN_CONFIG.tokens.usdt)
const TAKARA_MINT = new PublicKey(BLOCKCHAIN_CONFIG.tokens.takara)
const LAIKA_MINT = new PublicKey(BLOCKCHAIN_CONFIG.tokens.laika)

export default function InvestmentModal({
  isOpen,
  onClose,
  vaultId,
  calculation,
  usdtAmount,
  laikaAmount,
  takaraAmount = 0,
  takaraPrice = 0,
  boostType = 'LAIKA',
  acceptedPayments = 'USDT', // TAKARA is boost-only, not a payment method
}: InvestmentModalProps) {
  const { t } = useTranslation(['investment', 'common'])
  const { publicKey, sendTransaction } = useWallet()
  const { user } = useAuth()

  const queryClient = useQueryClient()
  const [step, setStep] = useState<Step>('review')
  const config = useConfig()

  // Get referral rates from API config (CASCADE v3.0: L1: 10%, L2: 6%, L3: 3%)
  const usdtReferralRates = config.referral.usdtRates
  const takaraReferralRates = config.referral.takaraRates

  // Calculate fees for Expected Returns (no Treasury Tax in calculator)
  const referralRateUSDT = user?.referralLevel ? (usdtReferralRates[user.referralLevel] || 0) * 100 : 0
  const referralRateTAKARA = user?.referralLevel ? (takaraReferralRates[user.referralLevel] || 0) * 100 : 0
  const referralFeeUSDT = calculation.earnings.totalUSDT * (referralRateUSDT / 100)
  const referralFeeTAKARA = calculation.mining.totalTAKARA * (referralRateTAKARA / 100)
  const netUSDT = calculation.earnings.totalUSDT - referralFeeUSDT
  const netTAKARA = calculation.mining.totalTAKARA - referralFeeTAKARA
  const [txSignature, setTxSignature] = useState<string>('')

  // Payment method is always USDT - TAKARA is boost-only
  const paymentMethod = 'USDT' as const

  const investMutation = useMutation({
    mutationFn: async () => {
      if (!publicKey || !sendTransaction) {
        throw new Error(t('investment:modal.connectWallet'))
      }

      // Re-validate vault status before proceeding (prevent race conditions)
      try {
        const vaultStatus = await api.getVaultById(vaultId)
        const vault = vaultStatus.data?.vault

        if (!vault) {
          throw new Error(t('common:errors.vaultNotFound'))
        }

        if (!vault.isActive) {
          throw new Error(t('common:errors.vaultInactive'))
        }

        // v3.3.2: Check capacity ONLY if totalCapacity is explicitly set (effectiveCapacity > 0)
        // If effectiveCapacity is undefined/null, vault accepts unlimited investments
        if (vault.effectiveCapacity && vault.effectiveCapacity > 0 && vault.remainingCapacity !== undefined && usdtAmount > vault.remainingCapacity) {
          throw new Error(
            `Insufficient vault capacity. Only $${vault.remainingCapacity.toLocaleString()} remaining before vault deactivates. Your investment of $${usdtAmount.toLocaleString()} exceeds the available space.`
          )
        }
      } catch (error: any) {
        // If it's already our custom error, re-throw it
        if (error.message && (error.message.includes('Vault') || error.message.includes('capacity'))) {
          throw error
        }
        // Otherwise, log and continue (network errors shouldn't block purchase)
        console.warn('Failed to re-validate vault status:', error)
      }

      const platformWallet = solanaService.getPlatformWalletAddress()
      const boostAmount = boostType === 'TAKARA' ? takaraAmount : laikaAmount

      // Build batch transfers - combine all token transfers in a single transaction
      const transfers: Array<{
        toPublicKey: PublicKey
        mintPublicKey: PublicKey
        amount: number
      }> = []

      if (paymentMethod === 'USDT') {
        // Add USDT payment transfer
        transfers.push({
          toPublicKey: platformWallet,
          mintPublicKey: USDT_MINT,
          amount: usdtAmount,
        })

        // Add required TAKARA transfer (if any)
        if (calculation.investment.requiredTAKARA > 0) {
          transfers.push({
            toPublicKey: platformWallet,
            mintPublicKey: TAKARA_MINT,
            amount: calculation.investment.requiredTAKARA,
          })
        }
      }

      // Add boost token transfer (LAIKA or TAKARA based on boostType)
      // Transfer boost token if boosting (LAIKA or TAKARA based on boostType)
      // v3.0: Each boost gets its own transaction signature
      console.log('🔍 Boost check:', { boostAmount, boostType, laikaAmount, takaraAmount, hasPublicKey: !!publicKey, hasSendTransaction: !!sendTransaction })
      if (boostAmount > 0) {
        const boostMint = boostType === 'TAKARA' ? TAKARA_MINT : LAIKA_MINT
        transfers.push({
          toPublicKey: platformWallet,
          mintPublicKey: boostMint,
          amount: boostAmount,
        })
      }

      // Execute all transfers in a single transaction
      let paymentSignature: string
      let confirmationTimedOut = false

      if (transfers.length > 0) {
        toast.info(`Transferring ${transfers.length} token(s) in a single transaction...`)

        try {
          paymentSignature = await solanaService.transferMultipleTokens(
            publicKey,
            transfers,
            sendTransaction
          )
          toast.success('All tokens transferred successfully!')
        } catch (transferError: any) {
          console.error('❌ Batch transfer failed:', transferError)

          // CRITICAL: If we got a timeout error, the transaction may still have succeeded
          // The solana.service.ts now returns the signature even on timeout
          // So this catch block won't be hit for timeout errors anymore
          throw transferError
        }
      } else {
        throw new Error('No tokens to transfer. Please check your investment configuration.')
      }

      setTxSignature(paymentSignature)

      // Create investment record with separate transaction signatures
      // Backend will verify the transaction signature on-chain
      toast.info('Creating investment and minting Wexel...')

      try {
        const response = await api.createInvestment({
          vaultId,
          usdtAmount,
          takaraAmount: calculation.investment.requiredTAKARA,
          takaraBoost: takaraAmount > 0
            ? {
                takaraAmount: takaraAmount,
                takaraValueUSD: takaraAmount * takaraPrice,
              }
            : undefined,
          laikaBoost: laikaAmount > 0
            ? {
                laikaAmount: laikaAmount,
                laikaValueUSD: calculation.investment.laikaValueUSD || 0,
              }
            : undefined,
          txSignature: paymentSignature, // Transaction signature (verified by backend)
          paymentMethod,
        })

        return response
      } catch (apiError: any) {
        console.error('❌ Investment creation failed:', apiError)

        // Show user-friendly error with transaction signature for support
        toast.error(
          `Investment creation failed. Transaction signature: ${paymentSignature.slice(0, 8)}...${paymentSignature.slice(-8)}. Please contact support if funds were transferred.`,
          { duration: 10000 }
        )

        throw apiError
      }
    },
    onSuccess: () => {
      setStep('success')
      toast.success('Investment created successfully!')
      queryClient.invalidateQueries({ queryKey: ['myInvestments'] })
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
    },
    onError: (error: any) => {
      // Reset to review step so user can try again
      setStep('review')

      // Parse error message
      let errorMessage = t('investment:modal.errors.investmentFailed')

      // Check for common error patterns
      if (error.message) {
        const msg = error.message.toLowerCase()

        // Insufficient funds errors
        if (msg.includes('insufficient') || msg.includes('0x1')) {
          errorMessage = t('investment:modal.errors.insufficientFunds')
        }
        // User rejected transaction
        else if (msg.includes('user rejected') || msg.includes('user denied')) {
          errorMessage = t('investment:modal.errors.userRejected')
        }
        // Timeout errors (check first, before generic network errors)
        else if (msg.includes('timeout')) {
          errorMessage = t('investment:modal.errors.timeout')
        }
        // Network/RPC errors
        else if (msg.includes('network')) {
          errorMessage = t('investment:modal.errors.network')
        }
        // Blockhash errors
        else if (msg.includes('blockhash')) {
          errorMessage = t('investment:modal.errors.blockhash')
        }
        // Backend validation errors
        else if (error.response?.data?.message) {
          errorMessage = error.response.data.message
        }
        // Default to the error message
        else {
          errorMessage = error.message
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      }

      toast.error(errorMessage)
      console.error('Investment error:', error)
    },
  })

  const handleInvest = async () => {
    setStep('transfer')
    try {
      await investMutation.mutateAsync()
    } catch (error) {
      setStep('review')
    }
  }

  const handleClose = () => {
    if (step !== 'transfer') {
      setStep('review')
      setTxSignature('')
      onClose()
    }
  }

  // Check BOTH token exists AND user data loaded (token is valid)
  const isFullyAuthenticated = api.isAuthenticated() && !!user

  if (!isOpen) return null

  // Safety guard: Don't allow investment if not fully authenticated (token + user data)
  if (!isFullyAuthenticated) {
    return null
  }

  // Wallet requirements - all payments via Phantom now
  const missingPhantom = !publicKey

  const isDisabled = investMutation.isPending || missingPhantom

  let buttonText = t('investment:modal.confirmButton')
  if (missingPhantom) buttonText = t('investment:modal.connectWallet')

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-background-card rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-green-900/20">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-green-900/20">
          <h2 className="text-2xl font-bold text-white">
            {step === 'success' ? t('investment:modal.success') : t('investment:modal.title', { vaultName: calculation.vault.name })}
          </h2>
          {step !== 'transfer' && (
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Review Step */}
          {step === 'review' && (
            <div className="space-y-6">
              {/* Payment Method - USDT Only */}
              <div className="bg-gradient-to-br from-gold-500/10 to-teal-500/10 border-2 border-gold-500/40 rounded-lg p-5">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">💰</span>
                  <div className="text-lg font-bold text-gold-400">
                    {t('common:paymentMethod')}
                  </div>
                </div>

                <div className="p-4 rounded-lg border-2 border-teal-500 bg-teal-500/20">
                  <div className="text-center">
                    <div className="text-2xl mb-2">💵</div>
                    <div className="font-bold text-teal-400">USDT</div>
                    <div className="text-sm text-gray-400">
                      ${usdtAmount.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      via Phantom (Solana)
                    </div>
                  </div>
                </div>

                <div className="bg-purple-500/10 border border-purple-500/30 rounded p-3 text-sm text-purple-300 mt-4">
                  <strong>{t('common:note')}:</strong> {t('wallet:phantom.note')}
                </div>
              </div>

              {/* TAKARA Entry Requirement (separate from payment method) */}
              {paymentMethod === 'USDT' && calculation.investment.requiredTAKARA > 0 && (
                <div className="bg-gradient-to-br from-gold-500/10 to-orange-500/10 border-2 border-gold-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xl">🔒</span>
                    <div className="text-sm font-bold text-gold-400">
                      {t('vaults:takaraRequirement.title')}
                    </div>
                  </div>
                  <div className="bg-black/30 rounded-lg p-3 mb-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">{t('vaults:takaraRequirement.required')}</span>
                      <span className="text-gold-400 font-bold text-lg">
                        {calculation.investment.requiredTAKARA.toLocaleString()} TKR
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {t('vaults:takaraRequirement.description')}
                    </div>
                  </div>
                  <div className="flex items-start gap-2 text-sm">
                    <span className="text-gold-400 mt-0.5">ℹ️</span>
                    <div className="text-gray-400">
                      {t('vaults:takaraRequirement.lockInfo')}
                    </div>
                  </div>
                </div>
              )}

              {/* Boost Info (LAIKA or TAKARA) */}
              {laikaAmount > 0 && (
                <div className={`${boostType === 'LAIKA' ? 'bg-gradient-laika/10 border-laika-purple/30' : 'bg-gold-500/10 border-gold-500/30'} border rounded-lg p-4`}>
                  <div className={`text-sm font-medium mb-2 ${boostType === 'LAIKA' ? 'text-laika-purple' : 'text-gold-400'}`}>
                    {boostType} Boost (via Phantom)
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-gray-400">{boostType} Amount</div>
                      <div className="text-white font-semibold">
                        {laikaAmount.toLocaleString()} {boostType}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-400">Boost USDT APY</div>
                      <div className={`font-semibold ${boostType === 'LAIKA' ? 'text-laika-green' : 'text-gold-400'}`}>
                        +{calculation.earnings.laikaBoostAPY}%
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Wallet Connection Status */}
              {missingPhantom && (
                <div className="bg-red-500/10 border-2 border-red-500/50 rounded-lg p-4">
                  <div className="text-sm text-red-400 font-bold mb-2">
                    {t('wallet:phantom.notConnected')}
                  </div>
                  <div className="text-sm text-gray-300">
                    <div className="flex items-center gap-2">
                      <span className="text-red-400">✗</span>
                      <span><strong>Phantom</strong> - {t('wallet:phantom.required')}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Connected Wallet - Simple confirmation */}
              {!missingPhantom && publicKey && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-sm text-green-400 font-medium mb-3">
                    <Wallet className="h-4 w-4" />
                    <span>{t('wallet:connected')}</span>
                  </div>
                  <div className="bg-black/20 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                      <span className="text-sm text-gray-300">Phantom</span>
                      <span className="text-xs text-gray-500">{publicKey.toBase58().slice(0, 6)}...{publicKey.toBase58().slice(-4)}</span>
                    </div>
                  </div>
                  <div className="text-xs text-gray-400 mt-2">
                    ✓ {t('wallet:verificationNote')}
                  </div>
                </div>
              )}

              {/* Investment Summary */}
              <div className="bg-background-elevated rounded-lg p-4 space-y-3">
                <h3 className="text-lg font-semibold text-white">{t('investment:modal.summary.title')}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-400">{t('investment:modal.summary.vault')}</div>
                    <div className="text-white font-medium">{calculation.vault.name}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">{t('common:duration')}</div>
                    <div className="text-white font-medium">
                      {t('common:months', { count: calculation.vault.duration })}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">{t('investment:modal.summary.amount')}</div>
                    <div className="text-white font-medium">
                      ${usdtAmount.toLocaleString()} USDT
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">{t('vaults:apy')}</div>
                    <div className="text-gold-500 font-bold text-lg">
                      {calculation.earnings.finalAPY}%
                    </div>
                  </div>
                  {/* Show TAKARA entry requirement if applicable */}
                  {paymentMethod === 'USDT' && calculation.investment.requiredTAKARA > 0 && (
                    <>
                      <div>
                        <div className="text-sm text-gray-400">{t('vaults:takaraRequirement.locked')}</div>
                        <div className="text-gold-400 font-medium">
                          {calculation.investment.requiredTAKARA.toLocaleString()} TKR
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-400">{t('common:totalPayment')}</div>
                        <div className="text-white font-medium">
                          ${usdtAmount.toLocaleString()} + {calculation.investment.requiredTAKARA.toLocaleString()} TKR
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Expected Returns - with fees */}
              <div className="bg-gold-500/10 border border-gold-500/30 rounded-lg p-4">
                <h4 className="text-sm text-gray-400 mb-3">{t('investment:modal.summary.estimatedReturns')}</h4>
                <div className="space-y-2">
                  {/* Gross earnings */}
                  <div className="flex justify-between">
                    <span className="text-gray-300">{t('vaults:earnings.usdt')} ({t('common:gross')})</span>
                    <span className="text-white font-semibold">
                      ${calculation.earnings.totalUSDT.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">{t('vaults:earnings.takara')} ({t('common:gross')})</span>
                    <span className="text-green-400 font-semibold">
                      {calculation.mining.totalTAKARA.toFixed(2)}
                    </span>
                  </div>

                  {/* ROI */}
                  <div className="flex justify-between border-t border-gold-500/30 pt-2 mt-2">
                    <span className="text-gray-300">{t('common:roi')}</span>
                    <span className="text-gold-500 font-bold text-lg">
                      {calculation.summary.roi}
                    </span>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <button
                onClick={handleInvest}
                disabled={isDisabled}
                className={`w-full py-4 rounded-lg font-semibold text-lg flex items-center justify-center gap-2 ${
                  isDisabled ? 'bg-gray-600 text-gray-400 cursor-not-allowed' : 'btn-gold'
                }`}
              >
                {buttonText}
                {!isDisabled && <ArrowRight className="h-5 w-5" />}
              </button>
            </div>
          )}

          {/* Transfer Step */}
          {step === 'transfer' && (
            <div className="text-center py-12 space-y-6">
              <Loader2 className="h-16 w-16 text-gold-500 animate-spin mx-auto" />
              <div>
                <h3 className="text-xl font-bold text-white mb-2">{t('investment:modal.processing')}</h3>
                <p className="text-gray-400">
                  {t('investment:modal.approveTransaction')}
                </p>
              </div>
            </div>
          )}

          {/* Success Step */}
          {step === 'success' && (
            <div className="text-center py-12 space-y-6">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
              <div>
                <h3 className="text-xl font-bold text-white mb-2">{t('investment:modal.success')}</h3>
                <p className="text-gray-400 mb-4">
                  {t('common:successMessage.investmentCreated')}
                </p>
                {txSignature && (
                  <div className="bg-background-elevated rounded-lg p-4 mb-4">
                    <div className="text-sm text-gray-400 mb-2">
                      {t('common:transactionSignature')}
                    </div>
                    <span className="text-green-400 text-sm break-all">
                      {txSignature}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    window.location.href = '/app/portfolio'
                  }}
                  className="btn-gold flex-1 py-3 rounded-lg font-semibold"
                >
                  {t('navigation:portfolio')}
                </button>
                <button
                  onClick={handleClose}
                  className="btn-outline-gold flex-1 py-3 rounded-lg font-semibold"
                >
                  {t('common:close')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
